import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ExternalChatButton } from "@/components/messaging/ExternalChatButton";
import { useTranslation } from "react-i18next";

interface PermissionCardProps {
  permission: any;
  isCoach: boolean;
  onRevoke?: (id: string) => void;
}

export const PermissionCard = ({ permission, isCoach, onRevoke }: PermissionCardProps) => {
  const { t } = useTranslation('supervision');

  // Gérer les types de permissions multiples
  const permissionTypes = permission.permission_types || [permission.permission_type];

  if (isCoach) {
    // Vue Coach
    return (
      <Card>
        <CardHeader>
          <div className="flex justify-between items-start">
            <div>
              <CardTitle className="text-lg">
                {permission.profiles?.username || t('coach.unknownTrainee')}
              </CardTitle>
              <p className="text-sm text-gray-600">
                {t('management.coach.permissions.supervises')}
              </p>
            </div>
            <div className="flex flex-wrap gap-1">
              {permissionTypes.map((type: string) => (
                <Badge key={type}>
                  {t(`management.permissionTypes.${type}`)}
                </Badge>
              ))}
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            <p className="text-sm text-gray-500">
              {t('management.coach.permissions.grantedOn')} {new Date(permission.granted_at).toLocaleDateString()}
            </p>
            <ExternalChatButton
              recipientId={permission.trainee_user_id}
              recipientName={permission.profiles?.username || t('coach.unknownTrainee')}
              recipientPhone={permission.profiles?.phone_number}
              recipientTelegram={permission.profiles?.telegram_username}
              whatsappEnabled={permission.profiles?.whatsapp_enabled}
              telegramEnabled={permission.profiles?.telegram_enabled}
              preferredPlatform={permission.profiles?.preferred_messaging_platform}
              messageContext="supervision"
            />
          </div>
        </CardContent>
      </Card>
    );
  } else {
    // Vue Stagiaire
    return (
      <Card>
        <CardHeader>
          <div className="flex justify-between items-start">
            <div>
              <CardTitle className="text-lg">
                {permission.coaches?.coach_name}
              </CardTitle>
              <p className="text-sm text-gray-600">
                {permission.coaches?.contact_email}
              </p>
            </div>
            <div className="flex flex-wrap gap-1">
              {permissionTypes.map((type: string) => (
                <Badge key={type}>
                  {t(`management.permissionTypes.${type}`)}
                </Badge>
              ))}
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            <div className="flex justify-between items-center">
              <p className="text-sm text-gray-500">
                {t('management.trainee.permissions.grantedOn')} {new Date(permission.granted_at).toLocaleDateString()}
              </p>
              {onRevoke && (
                <Button
                  variant="destructive"
                  size="sm"
                  onClick={() => onRevoke(permission.id)}
                >
                  {t('management.trainee.permissions.revokeAccess')}
                </Button>
              )}
            </div>
            <ExternalChatButton
              recipientId={permission.coach_id}
              recipientName={permission.coaches?.coach_name || t('coach.unknownCoach')}
              recipientPhone={permission.coaches?.phone_number}
              recipientTelegram={permission.coaches?.telegram_username}
              whatsappEnabled={permission.coaches?.whatsapp_enabled}
              telegramEnabled={permission.coaches?.telegram_enabled}
              preferredPlatform={permission.coaches?.preferred_messaging_platform}
              messageContext="supervision"
            />
          </div>
        </CardContent>
      </Card>
    );
  }
};